import React, { lazy, Suspense, useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Login from "./components/Login";
import AssumedAccessBanner from "./components/AssumedAccessBanner";
import SessionStatusBanner from "./components/SessionStatusBanner";
import PwaUpdateBanner from "./components/PwaUpdateBanner";
import { restoreSession, tokenNeedsRefresh } from "./services/session";
import { helpArticleBySlug } from "./services/helpAccess";
import { api } from "./services/api";
import { canAccessExternalConnections } from "./services/externalConnectionsAccess";
import {
  inspectionSubmissionEnabled,
  portfolioReportingEnabled,
} from "./services/servicePlanAccess";

const Dashboard = lazy(() => import("./components/Dashboard"));
const PushNotifications = lazy(() => import("./components/PushNotifications"));
const ClientDashboard = lazy(() => import("./components/ClientDashboard"));
const ClientProfitStatement = lazy(() => import("./components/ClientProfitStatement"));
const ScheduleConsultation = lazy(() => import("./components/ScheduleConsultation"));
const FormPage = lazy(() => import("./components/FormPage"));
const PropertyFormSettings = lazy(() => import("./components/PropertyFormSettings"));
const OrganizationFormSettings = lazy(() => import("./components/OrganizationFormSettings"));
const OrganizationSecurity = lazy(() => import("./components/OrganizationSecurity"));
const OrganizationOnboarding = lazy(() => import("./components/OrganizationOnboarding"));
const ServiceDeliverySettings = lazy(() => import("./components/ServiceDeliverySettings"));
const InviteRegistration = lazy(() => import("./components/InviteRegistration"));
const AdminSubmissions = lazy(() => import("./components/AdminSubmissions"));
const ForgotPassword = lazy(() => import("./components/ForgotPassword"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const AzRootsScheduler = lazy(() => import("./components/AzRootsScheduler"));
const DefaultScheduler = lazy(() => import("./components/Scheduler"));
const ResidentialForm = lazy(() => import("./components/ResidentialForm"));
const LongTermRental = lazy(() => import("./components/LongTermRental"));
const ShortTermRental = lazy(() => import("./components/ShortTermRental"));
const AccessInstructions = lazy(() => import("./components/AccessInstructions"));
const AZRaccessinstructions = lazy(() => import("./components/AZRaccessinstructions"));
const ProfitUpload = lazy(() => import("./components/ProfitUpload"));
const EditPropertyWrapper = lazy(() => import("./components/EditPropertyWrapper"));
const Billing = lazy(() => import("./components/Billing"));
const InvoiceReview = lazy(() => import("./components/InvoiceReview"));
const InvoiceEmailApproval = lazy(() => import("./components/InvoiceEmailApproval"));
const BidRequests = lazy(() => import("./components/BidRequests"));
const UserManagement = lazy(() => import("./components/UserManagement"));
const RouteManagement = lazy(() => import("./components/RouteManagement"));
const BulkOnboarding = lazy(() => import("./components/BulkOnboarding"));
const Reporting = lazy(() => import("./components/Reporting"));
const PlatformDashboard = lazy(() => import("./components/PlatformDashboard"));
const ResourceDashboard = lazy(() => import("./components/ResourceDashboard"));
const ExternalConnections = lazy(() => import("./components/ExternalConnections"));
const HelpCenter = lazy(() => import("./components/help/HelpCenter"));
const HelpArticle = lazy(() => import("./components/help/HelpArticle"));

function RouteLoading() {
  return (
    <div className="beta-page">
      <main className="beta-page-shell">
        <div className="beta-empty-state" role="status">Loading page…</div>
      </main>
    </div>
  );
}

function SchedulerWrapper() {
  // We check localStorage or a context/hook, whichever you prefer
  const role = localStorage.getItem("role");
  const orgName = localStorage.getItem("orgName");
  const isAzRootsAdmin = (role === "admin" && orgName === "AzRoots");

  // Return whichever component is appropriate
  if (isAzRootsAdmin) {
    return <AzRootsScheduler />;
  } else {
    return <DefaultScheduler />;
  }
}

function AccessInstructionsWrapper() {
  const role = localStorage.getItem("role");
  const orgName = localStorage.getItem("orgName");
  const isAzRootsAdmin = (role === "admin" && orgName === "AzRoots");

  if (isAzRootsAdmin) {
    return <AZRaccessinstructions />;
  } else {
    return <AccessInstructions />;
  }
}

function InvoiceReviewRoute({ user, role }) {
  const location = useLocation();
  if (!user) {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  return role === "property_manager"
    ? <InvoiceReview />
    : <Navigate to="/billing" replace />;
}

function HelpRoute({ user, children }) {
  const location = useLocation();
  const slug = location.pathname.match(/^\/help\/([^/]+)$/)?.[1] || "";
  const publicArticle = Boolean(helpArticleBySlug(slug)?.public);
  if (!user && !publicArticle) {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  return children;
}

function InspectionRoute({ user, serviceModel, accountScope, children }) {
  const location = useLocation();
  const assignmentId = new URLSearchParams(location.search).get("assignmentId") || "";
  if (!user) return <Navigate to="/" />;
  if (!inspectionSubmissionEnabled({ serviceModel, accountScope, assignmentId })) {
    return <Navigate to={accountScope === "afterlight_resource" ? "/resource" : "/dashboard"} replace />;
  }
  return children;
}

function BillingRoute({ user, role, accountScope }) {
  const [access, setAccess] = useState(null);
  useEffect(() => {
    let active = true;
    if (!user || role === "client" || accountScope === "afterlight_resource") {
      setAccess(false);
      return () => { active = false; };
    }
    api.get("/api/billing/access")
      .then((result) => {
        if (!active) return;
        const allowed = Boolean(result?.canAccess);
        localStorage.setItem("billingAccess", allowed ? "true" : "false");
        setAccess(allowed);
      })
      .catch(() => {
        if (!active) return;
        localStorage.setItem("billingAccess", "false");
        setAccess(false);
      });
    return () => { active = false; };
  }, [accountScope, role, user]);

  if (!user) return <Navigate to="/" />;
  if (access === null) return <RouteLoading />;
  return access ? <Billing /> : <Navigate to="/dashboard" replace />;
}

function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [platformRole, setPlatformRole] = useState(null);
  const [assumedOrganization, setAssumedOrganization] = useState(false);
  const [accountScope, setAccountScope] = useState(null);
  const [serviceModel, setServiceModel] = useState(null);
  const [portfolioReportingIncluded, setPortfolioReportingIncluded] = useState(true);

  useEffect(() => {
    const syncStoredSession = () => {
      setUser(Boolean(localStorage.getItem("token")));
      setRole(localStorage.getItem("role") || "user");
      setPlatformRole(localStorage.getItem("platformRole"));
      setAssumedOrganization(localStorage.getItem("assumedOrganization") === "true");
      setAccountScope(localStorage.getItem("accountScope") || "organization");
      setServiceModel(localStorage.getItem("serviceModel"));
      setPortfolioReportingIncluded(localStorage.getItem("portfolioReportingIncluded") !== "false");
    };
    const handleSessionCleared = () => {
      setUser(false);
      setRole(null);
      setPlatformRole(null);
      setAssumedOrganization(false);
      setAccountScope(null);
      setServiceModel(null);
      setPortfolioReportingIncluded(true);
    };
    window.addEventListener("auth-session-cleared", handleSessionCleared);
    window.addEventListener("auth-session-changed", syncStoredSession);
    return () => {
      window.removeEventListener("auth-session-cleared", handleSessionCleared);
      window.removeEventListener("auth-session-changed", syncStoredSession);
    };
  }, []);

  useEffect(() => {
    if (user === false) {
      setRole(null);
      setPlatformRole(null);
      setAssumedOrganization(false);
      setAccountScope(null);
      setServiceModel(null);
      setPortfolioReportingIncluded(true);
      return;
    }
    const token = localStorage.getItem("token");
    if (
      process.env.NODE_ENV !== "test" &&
      (!token || tokenNeedsRefresh(token, 0))
    ) {
      restoreSession().then((authenticated) => {
        setUser(authenticated);
        setRole(authenticated ? localStorage.getItem("role") || "user" : null);
        setPlatformRole(authenticated ? localStorage.getItem("platformRole") : null);
        setAssumedOrganization(
          authenticated && localStorage.getItem("assumedOrganization") === "true"
        );
        setAccountScope(authenticated ? localStorage.getItem("accountScope") || "organization" : null);
        setServiceModel(authenticated ? localStorage.getItem("serviceModel") : null);
        setPortfolioReportingIncluded(authenticated ? localStorage.getItem("portfolioReportingIncluded") !== "false" : true);
      });
      return;
    }

    if (!token) {
      setUser(false);
      setRole(null);
      setPlatformRole(null);
      setAssumedOrganization(false);
      setAccountScope(null);
      setServiceModel(null);
      setPortfolioReportingIncluded(true);
      return;
    }

    setUser(true);
    
    // Fetch role AFTER user is confirmed logged in
    const storedRole = localStorage.getItem("role");
    if (storedRole) {
      setRole(storedRole);
    } else {
      setRole("user");
    }
    setPlatformRole(localStorage.getItem("platformRole"));
    setAssumedOrganization(localStorage.getItem("assumedOrganization") === "true");
    setAccountScope(localStorage.getItem("accountScope") || "organization");
    setServiceModel(localStorage.getItem("serviceModel"));
    setPortfolioReportingIncluded(localStorage.getItem("portfolioReportingIncluded") !== "false");
  }, [user]);

  if (user === null) return null;

  const platformManagedAdminView = assumedOrganization
    && localStorage.getItem("organizationAdministrationMode") === "platform_managed";

  return (
    <>
    <AssumedAccessBanner />
    <PwaUpdateBanner />
    {user === true && <SessionStatusBanner />}
    {user === true && (!platformRole || assumedOrganization) && (
      <Suspense fallback={null}>
        <PushNotifications enabled />
      </Suspense>
    )}
    <Suspense fallback={<RouteLoading />}>
      <Routes>
      <Route path="/" element={!user
        ? <Login setUser={setUser} />
        : <Navigate to={platformRole && !assumedOrganization
          ? "/platform"
          : accountScope === "afterlight_resource" ? "/resource" : "/dashboard"} />} />
      <Route path="/join" element={<InviteRegistration />} />
      <Route path="/register" element={<Navigate to="/join" replace />} />
      <Route path="/login" element={user
        ? <Navigate to={platformRole && !assumedOrganization
          ? "/platform"
          : accountScope === "afterlight_resource" ? "/resource" : "/dashboard"} />
        : <Login setUser={setUser} />} />
      <Route path="/platform" element={
        user && platformRole === "platform_admin" && !assumedOrganization
          ? <PlatformDashboard />
          : <Navigate to="/" />
      } />
      <Route path="/resource" element={
        user && accountScope === "afterlight_resource"
          ? <ResourceDashboard setUser={setUser} />
          : <Navigate to="/" />
      } />

      {/* ✅ Ensure Clients Redirect Correctly */}
      <Route path="/external-connections" element={
        user && canAccessExternalConnections({ role, accountScope })
          ? <ExternalConnections setUser={setUser} />
          : <Navigate to="/" />
      } />
      <Route
        path="/dashboard"
        element={
          user
            ? accountScope === "afterlight_resource"
              ? <Navigate to="/resource" />
              : platformRole && !assumedOrganization
                ? <Navigate to="/platform" />
                : role === "client"
                  ? <Navigate to="/client/dashboard" />
                  : <Dashboard setUser={setUser} />
            : <Navigate to="/" />
        }
      />

      <Route path="/form/:property" element={
        <InspectionRoute user={user} serviceModel={serviceModel} accountScope={accountScope}>
          <FormPage />
        </InspectionRoute>
      } />
      <Route path="/property-form-settings/:property" element={user ? <PropertyFormSettings /> : <Navigate to="/" />} />
      <Route path="/organization-form-settings" element={user ? <OrganizationFormSettings /> : <Navigate to="/" />} />
      <Route path="/organization-security" element={
        user && role === "admin" && !assumedOrganization
          ? <OrganizationSecurity />
          : <Navigate to="/" />
      } />
      <Route path="/onboarding" element={
        user && role === "admin"
          ? <OrganizationOnboarding />
          : <Navigate to="/" />
      } />
      <Route path="/service-delivery" element={
        user && role === "admin"
          ? <ServiceDeliverySettings />
          : <Navigate to="/" />
      } />
      <Route path="/residential-form/:property" element={
        <InspectionRoute user={user} serviceModel={serviceModel} accountScope={accountScope}>
          <ResidentialForm />
        </InspectionRoute>
      } />
      <Route path="/long-term-rental-form/:property" element={
        <InspectionRoute user={user} serviceModel={serviceModel} accountScope={accountScope}>
          <LongTermRental />
        </InspectionRoute>
      } />
      <Route path="/short-term-rental-form/:property" element={
        <InspectionRoute user={user} serviceModel={serviceModel} accountScope={accountScope}>
          <ShortTermRental />
        </InspectionRoute>
      } />
      <Route path="/admin/submissions/:property" element={<AdminSubmissions />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/scheduler" element={user && ["admin", "property_manager"].includes(role) ? <SchedulerWrapper /> : <Navigate to="/" />} />
      <Route path="/billing" element={
        <BillingRoute user={user} role={role} accountScope={accountScope} />
      } />
      <Route path="/billing/review/:id" element={<InvoiceReviewRoute user={user} role={role} />} />
      <Route path="/billing/email-approval" element={<InvoiceEmailApproval />} />
      <Route path="/bid-requests" element={user && ["admin", "property_manager"].includes(role) ? <BidRequests /> : <Navigate to="/" />} />
      <Route path="/reporting" element={user
        && ["admin", "property_manager"].includes(role)
        && portfolioReportingEnabled({ serviceModel, portfolioReportingIncluded })
        ? <Reporting />
        : <Navigate to="/dashboard" replace />} />
      <Route path="/admin/users" element={user && role === "admin" ? <UserManagement /> : <Navigate to="/" />} />
      <Route path="/admin/routes" element={user && role === "admin" ? <RouteManagement /> : <Navigate to="/" />} />
      <Route path="/admin/bulk-onboarding" element={
        user && role === "admin" && (!assumedOrganization || platformManagedAdminView)
          ? <BulkOnboarding />
          : <Navigate to="/" />
      } />
      <Route path="/help" element={
        <HelpRoute user={user} platformRole={platformRole} assumedOrganization={assumedOrganization}>
          <HelpCenter />
        </HelpRoute>
      } />
      <Route path="/help/:slug" element={
        <HelpRoute user={user} platformRole={platformRole} assumedOrganization={assumedOrganization}>
          <HelpArticle />
        </HelpRoute>
      } />

      {/* Profit Uploads - Only for AzRoots Admins */}
      <Route path="/profit-uploads/:propertyName" element={user && role === "admin" ? <ProfitUpload /> : <Navigate to="/" />} />

      {/* Client Dashboard - Only for Clients */}
      <Route path="/client/dashboard" element={user && role === "client" ? <ClientDashboard setUser={setUser} /> : <Navigate to="/" />} />
      
      <Route path="/client/profit-statement/:propertyId" element={user && role === "client" ? <ClientProfitStatement /> : <Navigate to="/" />} />

      <Route path="/client/schedule-consultation" element={user && role === "client" ? <ScheduleConsultation /> : <Navigate to="/" />} />

      {/* Client Registration */}
      <Route path="/client-registration" element={<Navigate to="/join" replace />} />
     
      {/* AZRAccessinstructions conditional render */}
      <Route path="/access-instructions/:propertyName" element={<AccessInstructionsWrapper />} />
      <Route path="/admin/edit-property/:propertyName" element={user ? <EditPropertyWrapper /> : <Navigate to="/" />}/>
      <Route path="/azr-access-instructions/:propertyName" element={<AZRaccessinstructions />} />
      {/* 404 Redirect */}
      <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Suspense>
    </>
  );
}

export default App;
